import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Maximize,
  Minimize,
  Volume2,
  VolumeX,
  X,
  Radio,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Coffee,
  Monitor,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  LayoutGrid,
  Building,
  BarChart3,
  Pause,
  Play,
  ShieldAlert,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { formatTimeInTz, formatDateInTz, getActiveTimezone } from '../../lib/dateUtils';
import { sounds } from '../../lib/sounds';
import { haptics } from '../../lib/haptics';
import pulseLogo from '../../assets/pulse-logo.png';

interface WorkstationPresence {
  id: string;
  deviceId: string;
  hostname: string;
  deviceName: string;
  os: string;
  agentVersion: string;
  status: 'ACTIVE' | 'INACTIVE' | 'OFFLINE' | string;
  breakStatus?: 'NONE' | 'LUNCH_BREAK' | 'SHORT_BREAK' | 'PENDING_APPROVAL' | string;
  breakExpiresAt?: string | null;
  breakLabel?: string | null;
  breakRequestedDuration?: number;
  lastSeenAt: string;
  lastActivityAt: string | null;
  user: {
    id: string;
    fullName: string;
    email: string;
    department: string | null;
  } | null;
  activeInactivity: {
    id: string;
    startedAt: string;
    elapsedSeconds: number;
  } | null;
}

type KioskViewMode = 'grid' | 'matrix' | 'summary';

export function KioskPage() {
  const { user } = useAuth();
  const orgTimezone = getActiveTimezone(user?.organization?.timezone);
  const navigate = useNavigate();
  const [workstations, setWorkstations] = useState<WorkstationPresence[]>([]);
  const [metrics, setMetrics] = useState({ total: 0, active: 0, onBreak: 0, inactive: 0, offline: 0 });
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [audioAlerts, setAudioAlerts] = useState(true);
  const [autoRotate, setAutoRotate] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [viewMode, setViewMode] = useState<KioskViewMode>('grid');
  const [time, setTime] = useState(new Date());
  const [sseConnected, setSseConnected] = useState(false);
  const [tickerMessage, setTickerMessage] = useState('CK Pulse Edge Telemetry Online • Fleet presence synced');
  const [now, setNow] = useState(Date.now());
  const [rotateProgress, setRotateProgress] = useState(0);
  const previousStatusMap = useRef<Record<string, string>>({});

  const ITEMS_PER_PAGE = 12;
  const ROTATE_INTERVAL_MS = 15000;

  // Live clock and timer tick
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch initial data & setup SSE
  useEffect(() => {
    let mounted = true;

    const fetchSnapshot = async () => {
      try {
        const res = await api.admin.getLiveMonitor();
        if (!mounted) return;
        setWorkstations(res.workstations as WorkstationPresence[]);
        if (res.metrics) {
          setMetrics({
            total: res.metrics.total,
            active: res.metrics.active,
            onBreak: (res.metrics as any).onBreak || 0,
            inactive: res.metrics.inactive,
            offline: res.metrics.offline,
          });
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch kiosk snapshot', err);
        if (mounted) setLoading(false);
      }
    };

    fetchSnapshot();

    // SSE connection
    let eventSource: EventSource | null = null;
    try {
      const apiHost = import.meta.env.VITE_API_URL || '';
      eventSource = new EventSource(`${apiHost}/api/v1/admin/realtime`, { withCredentials: true });

      eventSource.onopen = () => {
        if (mounted) setSseConnected(true);
      };

      eventSource.onmessage = (e) => {
        try {
          const payload = JSON.parse(e.data);
          if (payload.type === 'SNAPSHOT' && payload.workstations) {
            if (mounted) {
              setWorkstations(payload.workstations);
              if (payload.metrics) {
                setMetrics({
                  total: payload.metrics.total,
                  active: payload.metrics.active,
                  onBreak: payload.metrics.onBreak || 0,
                  inactive: payload.metrics.inactive,
                  offline: payload.metrics.offline,
                });
              }

              // Acoustic radar chime on status transition
              payload.workstations.forEach((w: WorkstationPresence) => {
                const prev = previousStatusMap.current[w.id];
                if (prev && prev !== w.status) {
                  if (w.status === 'INACTIVE') {
                    if (audioAlerts) sounds.playAlertPing();
                    setTickerMessage(`⚠️ Inactivity Alert: ${w.user?.fullName || w.deviceName} is away from workstation`);
                  } else if (w.status === 'ACTIVE') {
                    if (audioAlerts) sounds.playChimeSound();
                    setTickerMessage(`🟢 Workstation Resumed: ${w.user?.fullName || w.deviceName} returned to desk`);
                  }
                }
                previousStatusMap.current[w.id] = w.status;
              });
            }
          }
        } catch (parseErr) {
          console.error('SSE parse error in Kiosk', parseErr);
        }
      };

      eventSource.onerror = () => {
        if (mounted) setSseConnected(false);
      };
    } catch {
      if (mounted) setSseConnected(false);
    }

    const pollInterval = setInterval(fetchSnapshot, 10000);

    return () => {
      mounted = false;
      clearInterval(pollInterval);
      if (eventSource) eventSource.close();
    };
  }, [audioAlerts]);

  // Fullscreen state listener
  useEffect(() => {
    const handleFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const toggleFullscreen = () => {
    haptics.selection();
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => console.error(err));
    } else {
      document.exitFullscreen().catch((err) => console.error(err));
    }
  };

  // Mutually exclusive status helpers
  const isWorkstationOnBreak = (w: WorkstationPresence) =>
    Boolean(
      w.breakStatus &&
      w.breakStatus !== 'NONE' &&
      w.status !== 'OFFLINE' &&
      (!w.breakExpiresAt || new Date(w.breakExpiresAt).getTime() > now)
    );

  const activeWorkstations = workstations.filter((w) => w.status === 'ACTIVE' && !isWorkstationOnBreak(w));
  const onBreakWorkstations = workstations.filter((w) => isWorkstationOnBreak(w));
  const inactiveWorkstations = workstations.filter((w) => w.status === 'INACTIVE' && !isWorkstationOnBreak(w));
  const offlineWorkstations = workstations.filter((w) => w.status === 'OFFLINE');

  // Auto-pagination carousel progress timer
  const totalPages = Math.ceil(workstations.length / ITEMS_PER_PAGE) || 1;

  useEffect(() => {
    if (!autoRotate || totalPages <= 1 || isHovered) {
      setRotateProgress(0);
      return;
    }

    const stepMs = 100;
    const progressInc = (stepMs / ROTATE_INTERVAL_MS) * 100;

    const timer = setInterval(() => {
      setRotateProgress((prev) => {
        if (prev >= 100) {
          setCurrentPage((curr) => (curr + 1) % totalPages);
          return 0;
        }
        return prev + progressInc;
      });
    }, stepMs);

    return () => clearInterval(timer);
  }, [autoRotate, totalPages, isHovered]);

  const pagedWorkstations = workstations.slice(
    currentPage * ITEMS_PER_PAGE,
    (currentPage + 1) * ITEMS_PER_PAGE
  );

  const getBreakCountdown = (expiresAt?: string | null) => {
    if (!expiresAt) return null;
    const remainingSecs = Math.max(0, Math.floor((new Date(expiresAt).getTime() - now) / 1000));
    if (remainingSecs <= 0) return 'Ending now';
    const m = Math.floor(remainingSecs / 60);
    const s = remainingSecs % 60;
    return `${m}m ${s < 10 ? '0' : ''}${s}s remaining`;
  };

  const getStatusBadge = (w: WorkstationPresence) => {
    if (isWorkstationOnBreak(w)) {
      const bLabel = w.breakLabel || (w.breakStatus === 'LUNCH_BREAK' ? 'LUNCH BREAK' : 'SHORT BREAK');
      return {
        label: bLabel.toUpperCase(),
        color: 'text-blue-400 bg-blue-950/60 border-blue-500/40 shadow-blue-900/30',
        dot: 'bg-blue-400',
        border: 'border-blue-500/30 shadow-[0_0_20px_rgba(59,130,246,0.12)]',
        isBreak: true,
      };
    }
    if (w.status === 'ACTIVE') {
      return {
        label: 'ACTIVE',
        color: 'text-emerald-400 bg-emerald-950/60 border-emerald-500/40 shadow-emerald-900/30',
        dot: 'bg-emerald-400 animate-pulse',
        border: 'border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.12)]',
      };
    }
    if (w.status === 'INACTIVE') {
      return {
        label: 'INACTIVE',
        color: 'text-rose-400 bg-rose-950/60 border-rose-500/40 shadow-rose-900/30',
        dot: 'bg-rose-400 animate-ping',
        border: 'border-rose-500/50 shadow-[0_0_25px_rgba(244,63,94,0.25)]',
      };
    }
    return {
      label: 'OFFLINE',
      color: 'text-zinc-400 bg-zinc-900/60 border-zinc-700/40',
      dot: 'bg-zinc-500',
      border: 'border-zinc-800',
    };
  };

  const getElapsedIdleText = (w: WorkstationPresence) => {
    const idleStarted = w.activeInactivity?.startedAt || w.lastActivityAt || w.lastSeenAt;
    if (!idleStarted) return '';
    const diff = Math.max(0, Math.floor((now - new Date(idleStarted).getTime()) / 1000));
    const m = Math.floor(diff / 60);
    const s = diff % 60;
    return `${m}m ${s < 10 ? '0' : ''}${s}s away`;
  };

  const activeRate = workstations.length > 0 ? Math.round((activeWorkstations.length / workstations.length) * 100) : 0;

  // Group by department for matrix view
  const departmentsMap = workstations.reduce((acc, w) => {
    const dept = w.user?.department || 'General Fleet';
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(w);
    return acc;
  }, {} as Record<string, WorkstationPresence[]>);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="min-h-screen bg-[#07090e] text-white flex flex-col font-sans select-none overflow-hidden"
    >
      {/* Top TV Kiosk Bar */}
      <header className="px-6 py-3.5 bg-[#0c1017]/95 backdrop-blur-2xl border-b border-zinc-800/80 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <img src={pulseLogo} alt="CK Pulse" className="w-9 h-9 object-contain drop-shadow-[0_0_12px_rgba(59,130,246,0.5)]" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold tracking-tight bg-gradient-to-r from-white via-zinc-200 to-zinc-400 bg-clip-text text-transparent">
                CK PULSE
              </h1>
              <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
                WALLBOARD 2.0
              </span>
            </div>
            <p className="text-xs text-zinc-400 flex items-center gap-1.5 mt-0.5">
              <span className={`w-2 h-2 rounded-full ${sseConnected ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
              {sseConnected ? 'Real-Time Edge Telemetry Active' : 'Connecting to Edge Stream...'}
            </p>
          </div>
        </div>

        {/* Global Live KPI Counters */}
        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center gap-3 px-3.5 py-1.5 rounded-2xl bg-zinc-900/60 border border-zinc-800">
            <div className="w-7 h-7 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Active Presence</div>
              <div className="text-base font-bold text-emerald-400">{activeWorkstations.length} / {workstations.length}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 px-3.5 py-1.5 rounded-2xl bg-zinc-900/60 border border-zinc-800">
            <div className="w-7 h-7 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
              <Coffee className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">On Break</div>
              <div className="text-base font-bold text-blue-400">{onBreakWorkstations.length}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 px-3.5 py-1.5 rounded-2xl bg-zinc-900/60 border border-zinc-800">
            <div className="w-7 h-7 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400">
              <AlertTriangle className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Inactive Now</div>
              <div className="text-base font-bold text-rose-400">{inactiveWorkstations.length}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 px-3.5 py-1.5 rounded-2xl bg-zinc-900/60 border border-zinc-800">
            <div className="w-7 h-7 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
              <TrendingUp className="w-3.5 h-3.5" />
            </div>
            <div>
              <div className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Coverage</div>
              <div className="text-base font-bold text-indigo-400">{activeRate}%</div>
            </div>
          </div>
        </div>

        {/* View Mode Switcher, Clock & Controls */}
        <div className="flex items-center gap-3">
          {/* View mode toggle */}
          <div className="hidden sm:flex items-center p-1 rounded-xl bg-zinc-900/80 border border-zinc-800 text-xs">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold transition ${
                viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              title="Fleet Carousel Grid"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              <span>Grid</span>
            </button>
            <button
              onClick={() => setViewMode('matrix')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold transition ${
                viewMode === 'matrix' ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              title="Department Matrix"
            >
              <Building className="w-3.5 h-3.5" />
              <span>Matrix</span>
            </button>
            <button
              onClick={() => setViewMode('summary')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-semibold transition ${
                viewMode === 'summary' ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
              title="Executive KPI Summary"
            >
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Summary</span>
            </button>
          </div>

          {/* Clock */}
          <div className="text-right">
            <div className="text-xl font-mono font-bold tracking-wider text-white">
              {formatTimeInTz(time, orgTimezone)}
            </div>
            <div className="text-[11px] text-zinc-400 font-mono">
              {formatDateInTz(time, orgTimezone)} • {orgTimezone.split('/')[1] || orgTimezone}
            </div>
          </div>

          <div className="flex items-center gap-1 p-1 rounded-2xl bg-zinc-900/80 border border-zinc-800">
            <button
              onClick={() => {
                haptics.selection();
                setAudioAlerts(!audioAlerts);
              }}
              title={audioAlerts ? 'Mute Radar Audio' : 'Enable Radar Audio'}
              className={`p-2 rounded-xl transition-all ${
                audioAlerts ? 'bg-blue-600/30 text-blue-400 border border-blue-500/40' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              {audioAlerts ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>

            <button
              onClick={() => setAutoRotate(!autoRotate)}
              title={autoRotate ? 'Pause Carousel Rotation' : 'Resume Carousel Rotation'}
              className={`p-2 rounded-xl transition-all ${
                autoRotate ? 'text-zinc-400 hover:text-white' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              }`}
            >
              {autoRotate ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>

            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit Fullscreen' : 'Enter TV Fullscreen (F11)'}
              className="p-2 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all"
            >
              {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </button>

            <button
              onClick={() => {
                haptics.selection();
                navigate('/admin/live');
              }}
              title="Close TV Kiosk"
              className="p-2 rounded-xl text-zinc-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area Based on View Mode */}
      <main className="flex-1 p-6 overflow-hidden flex flex-col justify-between">
        {loading ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <div className="w-12 h-12 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
            <p className="text-zinc-400 font-mono text-sm tracking-wide">Syncing fleet status for Wallboard display...</p>
          </div>
        ) : workstations.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
            <Monitor className="w-16 h-16 text-zinc-600 stroke-[1.5]" />
            <h3 className="text-xl font-bold text-zinc-300">No Registered Workstations</h3>
            <p className="text-sm text-zinc-500 max-w-md">
              Workstations will appear here automatically once enrolled and sending heartbeats.
            </p>
          </div>
        ) : viewMode === 'summary' ? (
          /* Executive Summary View */
          <div className="flex-1 flex flex-col justify-center max-w-6xl mx-auto w-full space-y-6 animate-ios-enter">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-6 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 flex flex-col justify-between">
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Fleet Operational Rate</span>
                <div className="text-5xl font-black text-emerald-400 mt-2">{activeRate}%</div>
                <p className="text-xs text-zinc-500 mt-2">{activeWorkstations.length} of {workstations.length} actively working</p>
              </div>

              <div className="p-6 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 flex flex-col justify-between">
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Resting / On Break</span>
                <div className="text-5xl font-black text-blue-400 mt-2">{onBreakWorkstations.length}</div>
                <p className="text-xs text-zinc-500 mt-2">Authorized policy break duration</p>
              </div>

              <div className="p-6 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 flex flex-col justify-between">
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Inactive Alerts</span>
                <div className="text-5xl font-black text-rose-400 mt-2">{inactiveWorkstations.length}</div>
                <p className="text-xs text-zinc-500 mt-2">Exceeding configured inactivity threshold</p>
              </div>

              <div className="p-6 rounded-3xl bg-zinc-900/60 border border-zinc-800/80 flex flex-col justify-between">
                <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider">Disconnected / Offline</span>
                <div className="text-5xl font-black text-zinc-500 mt-2">{offlineWorkstations.length}</div>
                <p className="text-xs text-zinc-500 mt-2">Signal lost &gt; 3 minutes</p>
              </div>
            </div>

            {/* High Priority Attention Queue */}
            <div className="p-6 rounded-3xl bg-zinc-900/40 border border-zinc-800/60">
              <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-300 flex items-center gap-2 mb-4">
                <ShieldAlert className="w-4 h-4 text-rose-500" />
                <span>Attention Required Workstations</span>
              </h3>
              {inactiveWorkstations.length === 0 ? (
                <div className="p-6 text-center text-xs text-zinc-500 font-mono">
                  All active workstations are within normal activity thresholds.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {inactiveWorkstations.map((iw) => (
                    <div
                      key={iw.id}
                      className="p-3.5 rounded-2xl bg-rose-950/20 border border-rose-500/30 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-rose-500/20 text-rose-400 flex items-center justify-center font-bold text-sm">
                          !
                        </div>
                        <div>
                          <div className="text-xs font-bold text-white truncate max-w-[140px]">
                            {iw.user?.fullName || iw.deviceName}
                          </div>
                          <div className="text-[10px] text-zinc-400">{iw.hostname} • {iw.user?.department || 'General'}</div>
                        </div>
                      </div>
                      <span className="text-xs font-mono font-bold text-rose-400">
                        {getElapsedIdleText(iw)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : viewMode === 'matrix' ? (
          /* Department Matrix View */
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {Object.entries(departmentsMap).map(([dept, devList]) => (
              <div key={dept} className="p-5 rounded-3xl bg-zinc-900/40 border border-zinc-800/60 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-blue-400" />
                    <h3 className="text-sm font-bold text-white tracking-wide">{dept}</h3>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-zinc-800 text-zinc-400">
                      {devList.length} PCs
                    </span>
                  </div>
                  <div className="text-xs text-zinc-400 font-mono">
                    {devList.filter((d) => d.status === 'ACTIVE' && !isWorkstationOnBreak(d)).length} Active •{' '}
                    {devList.filter((d) => isWorkstationOnBreak(d)).length} Break •{' '}
                    {devList.filter((d) => d.status === 'INACTIVE' && !isWorkstationOnBreak(d)).length} Inactive
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {devList.map((w) => {
                    const badge = getStatusBadge(w);
                    const isInactive = w.status.toUpperCase() === 'INACTIVE' && !isWorkstationOnBreak(w);
                    const onBreak = isWorkstationOnBreak(w);

                    return (
                      <div
                        key={w.id}
                        className={`p-3 rounded-2xl bg-[#0f141d] border flex flex-col justify-between ${badge.border} ${
                          isInactive ? 'ring-2 ring-rose-500/40' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.color}`}>
                            {badge.label}
                          </span>
                          <span className="text-[10px] font-mono text-zinc-500">{w.os?.includes('Win') ? 'Win' : 'PC'}</span>
                        </div>
                        <div className="mt-2">
                          <div className="text-xs font-bold text-white truncate">{w.user?.fullName || w.deviceName}</div>
                          <div className="text-[10px] text-zinc-400 truncate mt-0.5">{w.hostname}</div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-zinc-800/60 text-[10px] font-mono">
                          {isInactive ? (
                            <span className="text-rose-400 font-bold">{getElapsedIdleText(w)}</span>
                          ) : onBreak ? (
                            <span className="text-blue-400 font-bold">{getBreakCountdown(w.breakExpiresAt) || 'Break'}</span>
                          ) : (
                            <span className="text-zinc-500">Live</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Grid Carousel View (Default) */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-4 flex-1">
            {pagedWorkstations.map((w) => {
              const badge = getStatusBadge(w);
              const isInactive = w.status.toUpperCase() === 'INACTIVE' && !isWorkstationOnBreak(w);
              const onBreak = isWorkstationOnBreak(w);
              const idleTime = getElapsedIdleText(w);
              const breakTime = getBreakCountdown(w.breakExpiresAt);

              return (
                <div
                  key={w.id}
                  className={`relative rounded-3xl p-5 bg-[#0f141d]/90 backdrop-blur-xl border transition-all duration-300 flex flex-col justify-between ${
                    badge.border
                  } ${isInactive ? 'ring-2 ring-rose-500/50 animate-pulse-slow' : ''}`}
                >
                  {/* Top Status & Avatar */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wider border shadow-sm ${
                          badge.color
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${badge.dot}`} />
                        {badge.label}
                      </div>

                      <span className="text-[11px] font-mono text-zinc-500 truncate max-w-[80px]">
                        {w.os?.includes('Win') ? 'Windows' : 'Agent'}
                      </span>
                    </div>

                    {/* User Identity */}
                    <div className="flex items-center gap-3 mt-1">
                      <div
                        className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg text-white shadow-lg shrink-0 ${
                          isInactive
                            ? 'bg-gradient-to-br from-rose-600 to-amber-700'
                            : onBreak
                            ? 'bg-gradient-to-br from-blue-600 to-cyan-600'
                            : 'bg-gradient-to-br from-blue-600 to-indigo-700'
                        }`}
                      >
                        {w.user?.fullName ? w.user.fullName.charAt(0).toUpperCase() : w.deviceName.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-bold text-white truncate leading-tight">
                          {w.user?.fullName || w.deviceName}
                        </div>
                        <div className="text-xs text-zinc-400 truncate mt-0.5">
                          {w.user?.department || w.hostname}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Telemetry Detail */}
                  <div className="mt-4 pt-3 border-t border-zinc-800/60">
                    {isInactive ? (
                      <div className="bg-rose-500/15 border border-rose-500/30 rounded-xl px-2.5 py-1.5 flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-rose-400 shrink-0 animate-bounce" />
                        <span className="text-xs font-mono font-bold text-rose-300 truncate">
                          {idleTime || 'Inactivity Triggered'}
                        </span>
                      </div>
                    ) : onBreak ? (
                      <div className="bg-blue-500/15 border border-blue-500/30 rounded-xl px-2.5 py-1.5 flex items-center gap-2">
                        <Coffee className="w-3.5 h-3.5 text-blue-400 shrink-0 animate-pulse" />
                        <span className="text-xs font-mono font-bold text-blue-300 truncate">
                          {breakTime || 'Authorized Break'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between text-xs text-zinc-400">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 text-zinc-500" />
                          <span>Last Activity</span>
                        </span>
                        <span className="font-mono text-zinc-300">
                          {w.lastActivityAt
                            ? formatTimeInTz(w.lastActivityAt, orgTimezone, { hour: '2-digit', minute: '2-digit' })
                            : 'Just now'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom Ticker & Pagination Bar */}
        <footer className="mt-4 pt-3 border-t border-zinc-800/80 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0 text-xs">
          {/* Real-time Event Ticker */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-bold text-[10px] tracking-wider shrink-0 flex items-center gap-1">
              <Radio className="w-2.5 h-2.5 animate-pulse" />
              LIVE TICKER
            </span>
            <div className="font-mono text-zinc-400 truncate">
              {tickerMessage}
            </div>
          </div>

          {/* Pagination dots & Progress Bar */}
          {totalPages > 1 && viewMode === 'grid' && (
            <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
              <div className="w-24 h-1.5 bg-zinc-800 rounded-full overflow-hidden" title="Next Slide Progress">
                <div
                  className="h-full bg-blue-500 transition-all duration-100 ease-linear rounded-full"
                  style={{ width: `${rotateProgress}%` }}
                />
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1))}
                  className="p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: totalPages }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(idx)}
                      className={`h-1.5 rounded-full transition-all ${
                        currentPage === idx ? 'w-6 bg-blue-500' : 'w-2 bg-zinc-700 hover:bg-zinc-500'
                      }`}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setCurrentPage((prev) => (prev + 1) % totalPages)}
                  className="p-1 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              <span className="text-zinc-500 font-mono text-[11px]">
                {currentPage + 1}/{totalPages}
              </span>
            </div>
          )}
        </footer>
      </main>
    </div>
  );
}
